/**
 * Configuration for the formula editor.
 * Controls which operators, math functions, and constants are allowed,
 * and provides metadata for autocomplete labels.
 *
 * By DEFAULT (no restrictions), the editor works with ALL standard mathjs
 * expressions, operators, functions, and constants.
 * When restrictions are enabled, only the specified items are permitted.
 *
 * @see https://mathjs.org/docs/expressions/syntax.html
 * @see https://mathjs.org/docs/reference/functions.html
 * @see https://mathjs.org/docs/reference/constants.html
 */

import { FormulaEditorConfig } from '../models/formula-editor-config.model';
export { FormulaEditorConfig } from '../models/formula-editor-config.model';

// ---------------------------------------------------------------------------
// Operators — https://mathjs.org/docs/expressions/syntax.html
// ---------------------------------------------------------------------------

/** Operators organised by category */
export const FORMULA_OPERATORS_BY_CATEGORY: Record<string, Record<string, string>> = {
  arithmetic: {
    '+': 'Addition',
    '-': 'Subtraction',
    '*': 'Multiplication',
    '/': 'Division',
    '^': 'Power',
    '%': 'Modulo',
   '.^': 'Element-wise power', // X
   '.*': 'Element-wise multiply',// X
   './': 'Element-wise divide' // X
  },
  relational: {
    '==': 'Equal',
    '!=': 'Not equal',
    '<': 'Less than',
    '>': 'Greater than',
    '<=': 'Less or equal',
    '>=': 'Greater or equal'
  },
  logical: {
    and: 'Logical AND', // keyword form
    or: 'Logical OR',  // keyword form
    xor: 'Logical XOR', // X
    not: 'Logical NOT', // !
    '&&': 'Logical AND (inside ?: only)',
    '||': 'Logical OR (inside ?: only)'
  },
  bitwise: {
    '&': 'Bitwise AND', // X
    '|': 'Bitwise OR', // X
    '~': 'Bitwise NOT',// X
    '<<': 'Left shift', // X
    '>>': 'Right shift arithmetic', // X
    '>>>': 'Right shift logical'// X
  },
  other: {
    ':': 'Range / ternary else',
    to: 'Unit conversion',// X
    in: 'Unit conversion',// X
    '?': 'Conditional start (ternary)',
    '?:': 'Conditional ternary (?:)'
  }
};

/** Flat map of all operators with human-readable labels */
export const FORMULA_OPERATOR_LABELS: Record<string, string> = Object.values(FORMULA_OPERATORS_BY_CATEGORY).reduce(
  (acc, cat) => ({ ...acc, ...cat }),
  {} as Record<string, string>
);

// ---------------------------------------------------------------------------
// Functions — https://mathjs.org/docs/reference/functions.html
// ---------------------------------------------------------------------------

/** Functions organised by mathjs category */
export const FORMULA_FUNCTIONS_BY_CATEGORY: Record<string, Record<string, string>> = {
  arithmetic: {
    abs: 'Absolute value',
    add: 'Addition', // X
    cbrt: 'Cube root',
    ceil: 'Ceiling',
    cube: 'Cube',
    divide: 'Division', // X
    dotDivide: 'Element-wise division', // X
    dotMultiply: 'Element-wise multiplication', // X
    dotPow: 'Element-wise power',
    exp: 'Exponential',
    expm1: 'exp(x) − 1',
    fix: 'Round towards zero',
    floor: 'Floor',
    gcd: 'Greatest common divisor',
    hypot: 'Hypotenuse',
    lcm: 'Least common multiple',
    log: 'Logarithm',
    log10: 'Base-10 logarithm',
    log1p: 'ln(1 + x)',
    log2: 'Base-2 logarithm',
    mod: 'Modulus',
    multiply: 'Multiplication',
    norm: 'Norm',
    nthRoot: 'Nth root',
    pow: 'Power',
    round: 'Round',
    sign: 'Sign',
    sqrt: 'Square root',
    square: 'Square',
    subtract: 'Subtraction',
    unaryMinus: 'Unary minus',
    unaryPlus: 'Unary plus',
    xgcd: 'Extended GCD'
  },
  trigonometry: {
    acos: 'Arccosine',
    acosh: 'Hyperbolic arccosine',
    acot: 'Arccotangent',
    acoth: 'Hyperbolic arccotangent',
    acsc: 'Arccosecant',
    acsch: 'Hyperbolic arccosecant',
    asec: 'Arcsecant',
    asech: 'Hyperbolic arcsecant',
    asin: 'Arcsine',
    asinh: 'Hyperbolic arcsine',
    atan: 'Arctangent',
    atan2: 'Two-argument arctangent',
    atanh: 'Hyperbolic arctangent',
    cos: 'Cosine',
    cosh: 'Hyperbolic cosine',
    cot: 'Cotangent',
    coth: 'Hyperbolic cotangent',
    csc: 'Cosecant',
    csch: 'Hyperbolic cosecant',
    sec: 'Secant',
    sech: 'Hyperbolic secant',
    sin: 'Sine',
    sinh: 'Hyperbolic sine',
    tan: 'Tangent',
    tanh: 'Hyperbolic tangent'
  },
  statistics: {
    mad: 'Median absolute deviation',
    max: 'Maximum',
    mean: 'Mean',
    median: 'Median',
    min: 'Minimum',
    mode: 'Mode',
    prod: 'Product',
    quantileSeq: 'Quantile sequence',
    std: 'Standard deviation',
    sum: 'Sum',
    cumsum: 'Cumulative sum',
    variance: 'Variance'
  },
  probability: {
    combinations: 'Combinations',
    combinationsWithRep: 'Combinations with repetition',
    factorial: 'Factorial',
    gamma: 'Gamma function',
    kldivergence: 'Kullback–Leibler divergence',
    multinomial: 'Multinomial coefficient',
    permutations: 'Permutations',
    pickRandom: 'Pick random',
    random: 'Random number',
    randomInt: 'Random integer'
  },
  logical: {
    and: 'Logical AND',
    not: 'Logical NOT',
    or: 'Logical OR',
    xor: 'Logical XOR'
  },
  relational: {
    compare: 'Compare',
    compareNatural: 'Natural compare',
    compareText: 'Text compare',
    deepEqual: 'Deep equal',
    equal: 'Equal',
    equalText: 'Text equal',
    larger: 'Larger',
    largerEq: 'Larger or equal',
    smaller: 'Smaller',
    smallerEq: 'Smaller or equal',
    unequal: 'Unequal'
  },
  algebra: {
    derivative: 'Derivative',
    lsolve: 'Lower-triangular solve',
    lup: 'LU decomposition with pivoting',
    lusolve: 'LU solve',
    qr: 'QR decomposition',
    rationalize: 'Rationalize',
    simplify: 'Simplify',
    slu: 'Sparse LU decomposition',
    usolve: 'Upper-triangular solve'
  },
  bitwise: {
    bitAnd: 'Bitwise AND',
    bitNot: 'Bitwise NOT',
    bitOr: 'Bitwise OR',
    bitXor: 'Bitwise XOR',
    leftShift: 'Left shift',
    rightArithShift: 'Right arithmetic shift',
    rightLogShift: 'Right logical shift'
  },
  combinatorics: {
    bellNumbers: 'Bell numbers',
    catalan: 'Catalan numbers',
    composition: 'Composition',
    stirlingS2: 'Stirling numbers (2nd kind)'
  },
  complex: {
    arg: 'Argument (angle)',
    conj: 'Complex conjugate',
    im: 'Imaginary part',
    re: 'Real part'
  },
  geometry: {
    distance: 'Distance',
    intersect: 'Intersection point'
  },
  matrix: {
    apply: 'Apply',
    column: 'Column',
    concat: 'Concatenate',
    count: 'Count',
    cross: 'Cross product',
    ctranspose: 'Conjugate transpose',
    det: 'Determinant',
    diag: 'Diagonal',
    diff: 'Difference',
    dot: 'Dot product',
    eigs: 'Eigenvalues',
    expm: 'Matrix exponential',
    filter: 'Filter',
    flatten: 'Flatten',
    forEach: 'For each',
    getMatrixDataType: 'Matrix data type',
    identity: 'Identity matrix',
    inv: 'Inverse',
    kron: 'Kronecker product',
    map: 'Map',
    matrixFromColumns: 'Matrix from columns',
    matrixFromFunction: 'Matrix from function',
    matrixFromRows: 'Matrix from rows',
    ones: 'Ones matrix',
    partitionSelect: 'Partition select',
    pinv: 'Pseudo-inverse',
    range: 'Range',
    reshape: 'Reshape',
    resize: 'Resize',
    rotate: 'Rotate',
    rotationMatrix: 'Rotation matrix',
    row: 'Row',
    size: 'Size',
    sort: 'Sort',
    sqrtm: 'Matrix square root',
    squeeze: 'Squeeze',
    subset: 'Subset',
    trace: 'Trace',
    transpose: 'Transpose',
    zeros: 'Zeros matrix'
  },
  set: {
    setCartesian: 'Cartesian product',
    setDifference: 'Set difference',
    setDistinct: 'Distinct elements',
    setIntersect: 'Set intersection',
    setIsSubset: 'Is subset',
    setMultiplicity: 'Multiplicity',
    setPowerset: 'Power set',
    setSize: 'Set size',
    setSymDifference: 'Symmetric difference',
    setUnion: 'Set union'
  },
  special: {
    erf: 'Error function'
  },
  string: {
    bin: 'Binary string',
    format: 'Format',
    hex: 'Hexadecimal string',
    oct: 'Octal string',
    print: 'Print'
  },
  unit: {
    to: 'Convert units',
    createUnit: 'Create unit'
  },
  utils: {
    clone: 'Clone',
    hasNumericValue: 'Has numeric value',
    isInteger: 'Is integer',
    isNaN: 'Is NaN',
    isNegative: 'Is negative',
    isNumeric: 'Is numeric',
    isPositive: 'Is positive',
    isPrime: 'Is prime',
    isZero: 'Is zero',
    numeric: 'Convert to number',
    typeOf: 'Type of'
  },
  custom: {
    slopeShiftFn: 'Affine transform: value × slope + shift',
    minMaxFn: 'Scale value from read range to display range',
    deltaFn: 'Spread of values: max − min'
  }
};

/** Flat map of all functions with human-readable labels */
export const FORMULA_FUNCTION_LABELS: Record<string, string> = Object.values(FORMULA_FUNCTIONS_BY_CATEGORY).reduce(
  (acc, cat) => ({ ...acc, ...cat }),
  {} as Record<string, string>
);

// ---------------------------------------------------------------------------
// Constants — https://mathjs.org/docs/reference/constants.html
// ---------------------------------------------------------------------------

/** All mathjs built-in constants with human-readable labels */
export const FORMULA_CONSTANT_LABELS: Record<string, string> = {
  e: "Euler's number (≈ 2.718)",
  E: "Euler's number (≈ 2.718)",
  i: 'Imaginary unit',
  Infinity: 'Infinity',
  LN2: 'Natural log of 2',
  LN10: 'Natural log of 10',
  LOG2E: 'Base-2 log of e',
  LOG10E: 'Base-10 log of e',
  NaN: 'Not a Number',
  null: 'Null value',
  phi: 'Golden ratio (≈ 1.618)',
  pi: 'Pi (≈ 3.14159)',
  Pi: 'Pi (≈ 3.14159)',
  PI: 'Pi (≈ 3.14159)',// only PI
  SQRT1_2: '√(1/2)',
  SQRT2: '√2',
  tau: 'Tau (2π ≈ 6.283)',
  true: 'Boolean true',
  false: 'Boolean false',
  undefined: 'Undefined',
  version: 'mathjs version'
};

// ---------------------------------------------------------------------------
// Derived flat lists
// ---------------------------------------------------------------------------

/** Full list of all supported operators */
export const ALL_OPERATORS: string[] = Object.keys(FORMULA_OPERATOR_LABELS);

/**
 * Logical operators commonly restricted to a conditional (`?:`) context.
 * Use as a ready-made value for `FormulaEditorConfig.inConditionalOnlyOperators`.
 */
export const CONDITIONAL_ONLY_OPERATORS: string[] = ['and', 'or', 'xor', 'not', '&&', '||', '!'];

/**
 * Maps a shorthand operator token to the mathjs **function** it resolves to.
 *
 * Needed because mathjs reports `5!` as `op: '!'` / `fn: 'factorial'`: matching a
 * configured `!` against the token alone would misflag factorials.
 */
export const OPERATOR_TOKEN_ALIASES: Readonly<Record<string, string>> = {
  '&&': 'and',
  '||': 'or',
  '!': 'not'
};

/** Full list of all supported math functions */
export const ALL_FUNCTIONS: string[] = Object.keys(FORMULA_FUNCTION_LABELS);

/** Full list of all supported constants */
export const ALL_CONSTANTS: string[] = Object.keys(FORMULA_CONSTANT_LABELS);

// ---------------------------------------------------------------------------
// Default configuration — unrestricted
// ---------------------------------------------------------------------------

/** Default configuration — no restrictions, all operators, functions, and constants allowed */
export const DEFAULT_FORMULA_EDITOR_CONFIG: FormulaEditorConfig = {
  restrictOperators: false,
  allowedOperators: [...ALL_OPERATORS],
  restrictFunctions: false,
  allowedFunctions: [...ALL_FUNCTIONS],
  restrictConstants: false,
  allowedConstants: [...ALL_CONSTANTS]
};

// ---------------------------------------------------------------------------
// Preset: Basic arithmetic only (+, -, *, / and related functions)
// ---------------------------------------------------------------------------

/**
 * Restricted configuration that allows only basic arithmetic operators
 * (`+`, `-`, `*`, `/`) and their directly related math functions.
 *
 * Use this preset when the formula editor should only support simple
 * arithmetic expressions (e.g. `(var1 + var2) * var3 / var4`).
 *
 * Function selection criteria — only functions that map to allowed operators:
 *  - `+` → add, unaryPlus, sum, cumsum
 *  - `-` → subtract, unaryMinus
 *  - `*` → multiply, prod
 *  - `/` → divide
 *  - Operator-agnostic helpers: abs, round, ceil, floor, fix, sign, min, max, mean
 *
 * Excluded (require operators NOT in allowedOperators):
 *  - Power (`^`): sqrt, square, cbrt, pow, nthRoot, hypot, dotPow, cube
 *  - Modulo (`%`): mod, gcd, lcm, xgcd
 *  - Logarithmic / exponential: log, log2, log10, log1p, exp, expm1
 *  - Element-wise (`.*`, `./`, `.^`): dotMultiply, dotDivide, dotPow
 *  - Vector / matrix: norm
 */
export const BASIC_ARITHMETIC_FORMULA_CONFIG: FormulaEditorConfig = {
  restrictOperators: true,
  allowedOperators: ['+', '-', '*', '/'],
  restrictFunctions: true,
  allowedFunctions: [
    // arithmetic category — direct operator equivalents
    'add',        // +
    'subtract',   // -
    'multiply',   // *
    'divide',     // /
    'unaryPlus',  // +x
    'unaryMinus', // -x
    // arithmetic category — rounding helpers (operator-agnostic)
    'abs',
    'ceil',
    'floor',
    'round',
    'fix',
    'sign',
    // statistics category — composed of + and /
    'sum',
    'cumsum',
    'min',
    'max',
    'mean',
    'prod'
  ],
  restrictConstants: false,
  allowedConstants: [...ALL_CONSTANTS]
};


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns the effective list of allowed operators based on config */
export function getEffectiveOperators(config: FormulaEditorConfig): Set<string> {
  return new Set(config.restrictOperators ? config.allowedOperators : ALL_OPERATORS);
}

/** Returns the effective list of allowed functions based on config */
export function getEffectiveFunctions(config: FormulaEditorConfig): Set<string> {
  return new Set(config.restrictFunctions ? config.allowedFunctions : ALL_FUNCTIONS);
}

/** Returns the effective list of allowed constants based on config */
export function getEffectiveConstants(config: FormulaEditorConfig): Set<string> {
  return new Set(config.restrictConstants ? config.allowedConstants : ALL_CONSTANTS);
}

/** Returns the operators that are only permitted inside a conditional (`?:`) expression */
export function getInConditionalOnlyOperators(config: FormulaEditorConfig): Set<string> {
  return new Set(config.inConditionalOnlyOperators ?? []);
}

/**
 * Expands composite operator tokens to their component symbols.
 * For example, `'?:'` expands to `'?'` and `':'` so that the stream parser
 * can recognise individual characters when `?:` is in the allowed list.
 */
export function expandCompositeOperators(ops: Set<string>): Set<string> {
  const expanded = new Set(ops);
  if (expanded.has('?:')) {
    expanded.add('?');
    expanded.add(':');
  }
  return expanded;
}

